import { Link } from 'react-router-dom';
import { NextIcon, PauseIcon, PlayIcon, PreviousIcon } from './PlayerIcons';
import { formatDuration } from '../lib/audio';
import { usePlayer } from '../hooks/usePlayer';
import { Artwork } from './Artwork';

export function PlayerBar() {
  const {
    currentTrack,
    currentTime,
    duration,
    isPlaying,
    playPause,
    previous,
    next,
    setQueueOpen
  } = usePlayer();

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-background/88 px-2 py-1 pb-[max(0.35rem,env(safe-area-inset-bottom))] backdrop-blur-2xl sm:px-3 sm:py-2 sm:pb-[max(0.55rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto flex max-w-7xl flex-col gap-1 rounded-[0.95rem] border border-white/10 bg-card/88 px-2 py-1.5 shadow-glow sm:rounded-[1.6rem] sm:gap-2 sm:px-3 sm:py-2.5 lg:flex-row lg:items-center lg:gap-4">
        <div className="flex items-center gap-2 lg:contents">
          <Link to="/now-playing" className="flex min-w-0 flex-1 items-center gap-2 lg:w-[20rem] lg:flex-none">
            <Artwork track={currentTrack} size="sm" className="h-9 w-9 rounded-xl sm:h-12 sm:w-12 sm:rounded-2xl" />
            <div className="min-w-0">
              <p className="truncate text-[11px] font-semibold sm:text-sm">
                {currentTrack?.title ?? 'Nothing queued'}
              </p>
              <p className="truncate text-[10px] text-foreground/60 sm:text-[11px]">
                {currentTrack ? currentTrack.artist : 'Upload or queue a track'}
              </p>
            </div>
          </Link>
          <button
            type="button"
            onClick={() => void previous()}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-white/5 text-foreground transition hover:bg-white/10 sm:h-9 sm:w-9"
            aria-label="Previous track"
          >
            <PreviousIcon />
          </button>
          <button
            type="button"
            onClick={playPause}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent text-black sm:h-10 sm:w-10"
            aria-label={isPlaying ? 'Pause playback' : 'Start playback'}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <button
            type="button"
            onClick={() => void next()}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-white/5 text-foreground transition hover:bg-white/10 sm:h-9 sm:w-9"
            aria-label="Next track"
          >
            <NextIcon />
          </button>
          <button
            type="button"
            onClick={() => setQueueOpen(true)}
            className="inline-flex shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 px-2 py-1 text-left lg:flex-none sm:px-3 sm:py-1.5"
            aria-label="Open queue"
          >
            <span className="text-[8px] font-medium uppercase tracking-[0.12em] text-foreground/55 sm:text-[11px]">
              Queue
            </span>
          </button>
        </div>

        <div className="hidden min-w-0 flex-1 items-center gap-2 sm:flex">
          <span className="w-7 text-[10px] tabular-nums text-foreground/50 sm:w-8 sm:text-[11px]">
            {formatDuration(currentTime)}
          </span>
          <div className="h-1 flex-1 rounded-full bg-white/10 sm:h-1.5">
            <div
              className="h-1 rounded-full bg-accent transition-[width] sm:h-1.5"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="w-7 text-right text-[10px] tabular-nums text-foreground/50 sm:w-8 sm:text-[11px]">
            {formatDuration(duration)}
          </span>
        </div>
      </div>
    </div>
  );
}
